// Voice of Partner — auto-popup feedback modal.
//
// Mounted once at each portal's dashboard root (Medical + Lab). On
// mount, the active-feedback query fires; once it resolves and
// confirms there's an open cycle, the partner is eligible, and they
// haven't submitted yet — the dialog opens.
//
// Two close paths:
//   • Submit → mutation flips `hasSubmitted=true` server-side;
//     query refetches; modal stops auto-opening for this cycle even
//     on a fresh page load.
//   • Maybe later → local-only dismiss for this component lifecycle.
//     Navigating away and back re-mounts this component, so the
//     dialog reappears. Matches the "keep asking until they submit"
//     spec.
//
// `useGetActiveFeedbackQuery` is shared with PartnerFeedbackPage —
// RTK Query dedupes the request, so mounting both surfaces on the
// same dashboard costs one network call.

import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { MP, MONO, DISPLAY } from '../medical/shared';
import {
  formatCloseDate,
  getCountdown,
  CADENCE_LABEL,
} from './state';
import { useGetActiveFeedbackQuery } from '../../../store/feedbackApi';
import PartnerFeedbackForm from './PartnerFeedbackForm';

const PartnerFeedbackModal = () => {
  const { data, isSuccess } = useGetActiveFeedbackQuery();

  const cycle = data?.cycle || null;
  const hasSubmitted = !!data?.hasSubmitted;
  const eligible = data?.eligible !== false;
  const shouldShow = isSuccess
    && eligible
    && !!cycle
    && cycle.status === 'open'
    && !hasSubmitted;

  // Local-only dismiss tracking. This re-mounts (false → defaults
  // again) every time the dashboard re-mounts, so leaving the portal
  // and coming back re-pops the dialog as required by the spec.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (shouldShow) setOpen(true);
  }, [shouldShow]);

  const onCancel = () => setOpen(false);
  const onSubmitted = () => setOpen(false);

  // No cycle yet (still loading or none open) → don't render the
  // dialog skeleton at all. Saves a flash on first mount.
  if (!cycle) return null;

  const cadence = CADENCE_LABEL[cycle.cadence] || cycle.cadence;
  // `cycle.deadline` is an ISO string from the API; the formatters
  // expect numeric ms, so convert once.
  const deadlineMs = cycle.deadline ? new Date(cycle.deadline).getTime() : null;
  const closeFmt = deadlineMs ? formatCloseDate(deadlineMs) : '—';
  const countdown = deadlineMs ? getCountdown(deadlineMs) : '';

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: '14px',
          bgcolor: MP.white,
          maxHeight: '92vh',
          overflow: 'hidden',
          position: 'relative',
          // Yellow corner mark — mirrors the Open-Cycle modal on the
          // admin side so the two surfaces feel like one programme.
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0,
            width: 80, height: 4,
            bgcolor: MP.yellow,
            borderRadius: '0 0 4px 0',
            zIndex: 1,
          },
        },
      }}
      slotProps={{
        backdrop: { sx: { bgcolor: 'rgba(26,58,42,.55)', backdropFilter: 'blur(4px)' } },
      }}
    >
      {/* Header — eyebrow + title + close button */}
      <Box
        sx={{
          px: { xs: 3, md: 4 },
          pt: 3.5,
          pb: 2.5,
          borderBottom: `1px solid ${MP.border}`,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 11,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: MP.tealDark,
              mb: 1,
              fontWeight: 700,
            }}
          >
            Voice of Partner · {cycle.cycle_id}
          </Typography>
          <Typography
            sx={{
              fontFamily: DISPLAY,
              fontSize: { xs: 22, md: 26 },
              fontWeight: 800,
              letterSpacing: '0.03em',
              color: MP.dark,
              lineHeight: 1.05,
              mb: 0.75,
              textTransform: 'uppercase',
            }}
          >
            We'd love your feedback
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: MP.text, lineHeight: 1.55, maxWidth: '60ch' }}>
            <Box component="strong" sx={{ color: MP.dark, fontWeight: 700 }}>{cycle.name}</Box> is open for the
            current {cadence} cycle. Your inputs shape our service — three quick scores and a couple of
            open questions. Takes about three minutes.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onCancel} sx={{ color: MP.textMuted, mt: -0.5 }}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Cycle deadline banner — yellow strip across the top of the body */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{
          bgcolor: MP.yellowLight,
          borderBottom: `1px solid ${MP.yellow}`,
          px: { xs: 3, md: 4 },
          py: 1.25,
          fontFamily: MONO,
          fontSize: 11,
          letterSpacing: '0.04em',
          color: MP.dark,
        }}
      >
        <AccessTimeOutlinedIcon sx={{ fontSize: 16 }} />
        <Box>
          Closes <Box component="strong" sx={{ fontWeight: 700 }}>{closeFmt}</Box>
          <Box component="span" sx={{ mx: 0.5 }}>·</Box>
          {countdown}
          <Box component="span" sx={{ mx: 0.5 }}>·</Box>
          ~3 mins
        </Box>
      </Stack>

      {/* Body — the same form the sidebar page renders, just embedded
          so it doesn't double-frame the dialog with its own card
          borders. */}
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          bgcolor: MP.bg,
          overflowY: 'auto',
        }}
      >
        <PartnerFeedbackForm
          embedded={false}
          onSubmitted={onSubmitted}
          onCancel={onCancel}
        />
      </Box>
    </Dialog>
  );
};

export default PartnerFeedbackModal;
